import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { deDE, enUS, esES, frFR, itIT, plPL } from "@mui/x-data-grid-pro/locales";
import { de as deLocale } from "date-fns/locale/de";
import { enUS as enLocale } from "date-fns/locale/en-US";
import { es as esLocale } from "date-fns/locale/es";
import { fr as frLocale } from "date-fns/locale/fr";
import { it as itLocale } from "date-fns/locale/it";
import { pl as plLocale } from "date-fns/locale/pl";
import i18n from "i18next";
import React, { useEffect, useMemo } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { isPublicRoute as checkIsPublicRoute } from "../config/publicRoutes";
import { selectLoggedInArtistID } from "../store/auth/authSelector";
import { loadCommonAction } from "../store/common/commonActions";
import { selectHistory } from "../store/router/routerSelectors";
import { selectIsCommonStateLoading } from "../store/common/commonSelector";
import LoginArtistContainer from "./auth/LoginArtistContainer";
import LoginStudioContainer from "./auth/LoginStudioContainer";
import SignupContainer from "./auth/SignupContainer";
import BackendErrorContainer from "./error/BackendErrorContainer";

import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, responsiveFontSizes, ThemeProvider } from "@mui/material/styles";
import { cleanupArtistRecentUsage } from "../api/localArtistPersistence";
import { useAppThemeMode } from "../hooks/useAppThemeMode";
import AppointmentPersistenceProvider from "../appointment/AppointmentPersistenceContext";
import { useEventLogger } from "../hooks/useEventLogger";
import { useLoggedInArtistLocale } from "../hooks/useLoggedInArtistLocale";
import { clearActiveNotificationAction } from "../store/notification/notificationAction";
import { selectAllButInfoNotification } from "../store/notification/notificationSelector";
import { clearHistory } from "../store/router/routerActions";
import ColorModeContext from "./common/ColorModeContext";
import LoadingIndicator from "./common/LoadingIndicator";
import { useAffiliateCapture } from "../hooks/useAffiliateCapture";
import {
    ANALYTICS_BASEPATH,
    AUTO_MESSAGE_SETTINGS_SUBPATH,
    CUSTOMER_PORTAL_BASEPATH,
    EMAIL_DESIGNS_SUBPATH,
    FORMS_EDITOR_PATH,
    FORMS_TEMPLATE_SUBPATH,
    KISSSIGN_AUTO_DELIVERY_SUBPATH,
    KISSSIGN_BASEPATH,
    KUNDEN_IMPORT,
    MESSAGES_SUBPATH,
    MESSAGE_TEMPLATE_NEW_SUBPATH,
    SEND_MESSAGE_PATH,
    SHIFT_PLANNING_PATH,
    STUDIO_APPOINTMENT_TYPES_SUBPATH,
    STUDIO_ASSETS_SUBPATH,
    STUDIO_BASE_SUBPATH,
    STUDIO_BASEPATH,
    STUDIO_GGL_SYNC_SUBPATH,
    STUDIO_INVOICES_SUBPATH,
    STUDIO_LOCALIZATION_SUBPATH,
    STUDIO_LOCATIONS_SUBPATH,
    STUDIO_MAIL_ADDRESSES_SUBPATH,
    STUDIO_SHIFT_CONFIGURATION_SUBPATH,
    STUDIO_SUBSCRIPTION_SUBPATH,
    STUDIO_V2_API_SUBPATH,
    TEMPLATE_BASE_PATH,
    TERMIN_ICALIMPORT,
    VOUCHERS_SUBPATH,
} from "./componentPaths";
import { useLoggedInArtistRights } from "../hooks/useLoggedInArtistRights";
import NeuKundeDoublettenCheckContainer from "./kunde/doubletten/NeuKundeDoublettenCheckContainer";
import SessionNotification from "./notification/SessionNotification";
import TimelineContainer from "./timeline/TimelineContainer";
import { useBackendVersionSync } from "./useBackendVersionSync";
import AppErrorBoundary from "./error/AppErrorBoundary";
import {
    AnalyticsContainer,
    AppointmentPage,
    AppointmentTypesContainer,
    ArtistEditAssistContainer,
    ArtistEditBaseContainer,
    ArtistEditLocationContainer,
    ArtistEditMailContainer,
    ArtistEditRightsContainer,
    ArtistHomeContainer,
    ArtistListContainer,
    ArtistWorktimesContainer,
    AutoMessageSettingsContainer,
    CalendarContainer,
    CustomerPortalContainer,
    DblExclusionsListContainer,
    DokuVersandContainer,
    EditStudioAboContainer,
    EditStudioBaseContainer,
    EmailDesignEditContainer,
    EmailDesignListContainer,
    FormEditContainer,
    FormsListContainer,
    GoogleSyncStudioSetting,
    ICalImportContainer,
    JobsListContainer,
    KissSignHomeContainer,
    KundenDoublettenListContainer,
    KundenImportContainer,
    KundenListContainer,
    KundenTerminListContainer,
    KundeEditContainer,
    KundeSearchContainer,
    ListContainer,
    ListEinnahmenContainer,
    LocationListContainer,
    MailAddressesContainer,
    MessageTemplateEditContainer,
    MessageTemplateListContainer,
    NachrichtListContainer,
    NewFinalizerContainer,
    PadContainer,
    ProjektListContainer,
    SendNachrichtContainer,
    ShiftConfigurationContainer,
    ShiftPlanningContainer,
    StudioAssetGalleryContainer,
    StudioHomeContainer,
    StudioInvoiceListContainer,
    StudioLocalization,
    SwaggerUI,
    TerminListContainer,
    V2ApiSettings,
    VoucherOutListContainer,
    VoucherTemplateListContainer,
    VouchInListContainer,
    VorlagenHomeContainer,
} from "./lazyRouteComponents";

const localeMap = {
    en: enLocale,
    de: deLocale,
    fr: frLocale,
    it: itLocale,
    es: esLocale,
    pl: plLocale,
};

const dataGridTranslationMap = {
    de: deDE,
    en: enUS,
    fr: frFR,
    it: itIT,
    es: esES,
    pl: plPL,
}

const App = () => {
    const { capturePathEvents } = useEventLogger();
    const history = useSelector((state) => selectHistory(state));
    const location = useLocation();
    useAffiliateCapture();
    const locale = useLoggedInArtistLocale()
    const loggedInArtistID = useSelector((state) => selectLoggedInArtistID(state));
    const { mode, colorMode } = useAppThemeMode(loggedInArtistID);
    const activeNotification = useSelector((state) => selectAllButInfoNotification(state));
    const navigate = useNavigate();
    const dispatch = useDispatch();
    useBackendVersionSync();
    const isCommonStateLoading = useSelector((state) => selectIsCommonStateLoading(state));
    const isStateLoaded = !isCommonStateLoading;
    const { isReady: isRightsReady, viewableArtistIdList: readableArtistIds } = useLoggedInArtistRights();
    const cleanupPerformedRef = React.useRef({ artistId: null, performed: false });

    // Public routes that don't require authentication
    const isPublicRoute = useMemo(() => {
        return checkIsPublicRoute(location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        if (location) {
            capturePathEvents(location.pathname);
        }
    }, [capturePathEvents, location]);

    const theme = React.useMemo(() => {
        const dataGridBundle = dataGridTranslationMap[locale] || dataGridTranslationMap.en;
        const dataGridLocaleText = dataGridBundle?.components?.MuiDataGrid?.defaultProps?.localeText;
        let newTheme = createTheme({
            palette: {
                mode,
            },
            components: {
                MuiInputBase: {
                    styleOverrides: {
                        input: {
                            // Überschreiben für Input-Felder vom Typ number:
                            "&[type=number]": {
                                MozAppearance: "textfield", // Firefox
                                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                                    WebkitAppearance: "none", // Chrome, Safari, Edge, Opera
                                    margin: 0,
                                },
                            },
                        },
                    },
                },
                MuiDataGrid: {
                    defaultProps: {
                        localeText: dataGridLocaleText,
                    },
                },
            },
        });
        return responsiveFontSizes(newTheme);
    }, [mode, locale]);

    useEffect(() => {
        if (history) {
            navigate(history);
            dispatch(clearHistory());
        }
    }, [history, dispatch, navigate]);

    useEffect(() => {
        // Only bootstrap for protected routes
        if (!isPublicRoute) {
            dispatch(loadCommonAction());
        }
    }, [dispatch, isPublicRoute]);

    useEffect(() => {
        // Only set artist locale for protected routes when artist is logged in
        // Public routes (login/signup) use browser language detection from i18n.js
        if (!isPublicRoute && loggedInArtistID && locale) {
            i18n.changeLanguage(locale);
        }
    }, [locale, isPublicRoute, loggedInArtistID]);

    // Cleanup recent artist usage once per session per user
    // Remove artists that the logged-in user no longer has access to
    useEffect(() => {
        // Reset cleanup flag when artist ID changes (new login)
        if (loggedInArtistID !== cleanupPerformedRef.current.artistId) {
            cleanupPerformedRef.current = { artistId: loggedInArtistID, performed: false };
        }

        if (
            !isPublicRoute &&
            loggedInArtistID &&
            isStateLoaded &&
            isRightsReady &&
            !cleanupPerformedRef.current.performed
        ) {
            cleanupArtistRecentUsage(loggedInArtistID, readableArtistIds);
            cleanupPerformedRef.current.performed = true;
        }
    }, [isPublicRoute, loggedInArtistID, isStateLoaded, isRightsReady, readableArtistIds]);

    const handleCloseNotification = () => dispatch(clearActiveNotificationAction());

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[locale] || localeMap.en}>
            <AppointmentPersistenceProvider>
                <ColorModeContext value={colorMode}>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <AppErrorBoundary resetKey={location.pathname}>
                        <React.Suspense fallback={<LoadingIndicator message="Loading..." />}>
                        <Routes>
                            <Route path="/" exact element={<CalendarContainer />} />
                            <Route path="/appointment/neu" exact element={<AppointmentPage neu />} />
                            <Route path="/appointment/:terminId" exact element={<AppointmentPage />} />
                            <Route path={TERMIN_ICALIMPORT} exact element={<ICalImportContainer />} />
                            <Route path="/finalizer" exact element={<NewFinalizerContainer />} />
                            <Route path="/login-studio" exact element={<LoginStudioContainer />} />
                            <Route path={"/login-artist"} exact element={<LoginArtistContainer />} />
                            <Route path="/signup" exact element={<SignupContainer />} />
                            <Route path={"/kunde/neu"} exact element={<NeuKundeDoublettenCheckContainer />} />
                            <Route path={"/kunde/suche"} exact element={<KundeSearchContainer />} />
                            <Route path={KUNDEN_IMPORT} exact element={<KundenImportContainer />} />
                            <Route path={"/kunde/:kundid/termine"} exact element={<KundenTerminListContainer />} />
                            <Route path={"/kunde/:kundid"} exact element={<KundeEditContainer />} />
                            <Route path={"/artist/:artistid/base"} exact element={<ArtistEditBaseContainer />} />
                            <Route path={"/artist/:artistid/mail"} exact element={<ArtistEditMailContainer />} />
                            <Route
                                path={"/artist/:artistid/hours"}
                                exact
                                element={<ArtistWorktimesContainer />}
                            />
                            <Route
                                path={"/artist/:artistid/locations"}
                                exact
                                element={<ArtistEditLocationContainer />}
                            />
                            <Route path={"/artist/:artistid/rights"} exact element={<ArtistEditRightsContainer />} />
                            <Route path={"/artist/:artistid/assist"} exact element={<ArtistEditAssistContainer />} />
                            <Route path={"/artists"} exact element={<ArtistListContainer />} />
                            <Route path={"/artists/:artistid"} exact element={<ArtistHomeContainer />}>
                                <Route index element={<ArtistEditBaseContainer />} />
                                <Route path={"base"} exact element={<ArtistEditBaseContainer />} />
                                <Route path={"mail"} exact element={<ArtistEditMailContainer />} />
                                <Route path={"hours"} exact element={<ArtistWorktimesContainer />} />
                                <Route path={"locations"} exact element={<ArtistEditLocationContainer />} />
                                <Route path={"rights"} exact element={<ArtistEditRightsContainer />} />
                                <Route path={"assist"} exact element={<ArtistEditAssistContainer />} />
                            </Route>
                            <Route path={TEMPLATE_BASE_PATH} exact element={<VorlagenHomeContainer />}>
                                <Route index element={<MessageTemplateListContainer />} />
                                <Route
                                    path={AUTO_MESSAGE_SETTINGS_SUBPATH}
                                    element={<AutoMessageSettingsContainer />}
                                />
                                <Route
                                    path={`${MESSAGES_SUBPATH}/${MESSAGE_TEMPLATE_NEW_SUBPATH}`}
                                    element={<MessageTemplateEditContainer neu />}
                                />
                                <Route
                                    path={`${MESSAGES_SUBPATH}/:msgVorlageId`}
                                    element={<MessageTemplateEditContainer />}
                                />
                                <Route path={EMAIL_DESIGNS_SUBPATH} element={<EmailDesignListContainer />} />
                                <Route
                                    path={`${EMAIL_DESIGNS_SUBPATH}/:emailDesignId`}
                                    element={<EmailDesignEditContainer />}
                                />
                                <Route path={STUDIO_ASSETS_SUBPATH} element={<StudioAssetGalleryContainer />} />
                                <Route path={VOUCHERS_SUBPATH} element={<VoucherTemplateListContainer />} />
                                <Route path={FORMS_TEMPLATE_SUBPATH} element={<FormsListContainer />} />
                            </Route>
                            <Route path={`${FORMS_EDITOR_PATH}/:formsId`} element={<FormEditContainer />} />
                            <Route path={STUDIO_BASEPATH} exact element={<StudioHomeContainer />}>
                                <Route index element={<EditStudioBaseContainer />} />
                                <Route path={STUDIO_BASE_SUBPATH} element={<EditStudioBaseContainer />} />
                                <Route path={STUDIO_SUBSCRIPTION_SUBPATH} element={<EditStudioAboContainer />} />
                                <Route path={STUDIO_LOCATIONS_SUBPATH} element={<LocationListContainer />} />
                                <Route path={STUDIO_MAIL_ADDRESSES_SUBPATH} element={<MailAddressesContainer />} />
                                <Route
                                    path={STUDIO_APPOINTMENT_TYPES_SUBPATH}
                                    element={<AppointmentTypesContainer />}
                                />
                                <Route
                                    path={STUDIO_SHIFT_CONFIGURATION_SUBPATH}
                                    element={<ShiftConfigurationContainer />}
                                />
                                <Route path={STUDIO_GGL_SYNC_SUBPATH} element={<GoogleSyncStudioSetting />} />
                                <Route path={STUDIO_V2_API_SUBPATH} element={<V2ApiSettings />} />
                                <Route path={STUDIO_INVOICES_SUBPATH} element={<StudioInvoiceListContainer />} />
                                <Route path={STUDIO_LOCALIZATION_SUBPATH} element={<StudioLocalization />} />
                            </Route>
                            <Route path={"/lists/kunde/:reportdefid"} exact element={<KundenListContainer />} />
                            <Route
                                path={"/lists/dblexclusions/:reportdefid"}
                                exact
                                element={<DblExclusionsListContainer />}
                            />
                            <Route
                                path={"/lists/doubletten/:reportdefid"}
                                exact
                                element={<KundenDoublettenListContainer />}
                            />
                            <Route path={"/lists/projekt/:reportdefid"} exact element={<ProjektListContainer />} />
                            <Route path={"/lists/termin/:reportdefid"} exact element={<TerminListContainer />} />
                            <Route path={"/lists/einnahmen/:reportdefid"} exact element={<ListEinnahmenContainer />} />
                            <Route path={"/lists/jobs/:reportdefid"} exact element={<JobsListContainer />} />
                            <Route path={"/lists/vouchers/:reportdefid"} exact element={<VoucherOutListContainer />} />
                            <Route path={"/lists/vouchin/:reportdefid"} exact element={<VouchInListContainer />} />
                            <Route
                                path={"/lists/nachrichten/:reportdefid"}
                                exact
                                element={<NachrichtListContainer />}
                            />
                            <Route path={SEND_MESSAGE_PATH} exact element={<SendNachrichtContainer />} />
                            <Route path={KISSSIGN_BASEPATH} exact element={<KissSignHomeContainer />}>
                                <Route index element={<PadContainer />} />
                                <Route path={KISSSIGN_AUTO_DELIVERY_SUBPATH} element={<DokuVersandContainer />} />
                            </Route>
                            <Route path={"/pads"} exact element={<PadContainer />} />
                            <Route path={CUSTOMER_PORTAL_BASEPATH} exact element={<CustomerPortalContainer />} />
                            <Route path={ANALYTICS_BASEPATH} exact element={<AnalyticsContainer />} />
                            <Route path={"/lists"} exact element={<ListContainer />} />
                            <Route path={"/timeline"} exact element={<TimelineContainer />} />
                            <Route path={SHIFT_PLANNING_PATH} exact element={<ShiftPlanningContainer />} />
                            <Route path={"/swagger-ui"} exact element={<SwaggerUI />} />
                            <Route path={"/error"} exact element={<BackendErrorContainer />} />
                        </Routes>
                        <SessionNotification
                            onClose={handleCloseNotification}
                            open={activeNotification !== undefined}
                            message={activeNotification?.message}
                            severity={activeNotification?.severity}
                        />
                    </React.Suspense>
                        </AppErrorBoundary>
                </ThemeProvider>
            </ColorModeContext>
        </AppointmentPersistenceProvider>
        </LocalizationProvider>
    );
};

export default connect()(App);
